/**
 * recentBookingsCache.js
 * 
 * Gerencia o cache local de agendamentos recentes que ainda não foram 
 * totalmente persistidos no Firebase. Isso permite atualizações otimistas
 * da UI enquanto os dados são salvos no backend.
 */

import { format } from 'date-fns';
import { queryClient, queryKeys } from './queryClient';

// Cache local de reservas temporárias (TTL = 5 minutos)
const temporaryBookings = new Map();

// TTL padrão para reservas temporárias (5 minutos em ms)
const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * Adiciona uma reserva temporária ao cache local
 * 
 * @param {Object} booking - Dados do agendamento
 * @param {number} ttl - Tempo de vida do cache em ms (padrão: 5 minutos)
 * @returns {string} - ID único da reserva temporária
 */
export const addTemporaryBooking = (booking, ttl = DEFAULT_TTL) => {
  if (!booking || !booking.salonId || !booking.date || !booking.time || !booking.professionalId) {
    console.error('[recentBookingsCache] Dados de agendamento inválidos:', booking);
    throw new Error('Dados de agendamento inválidos');
  }

  // Gerar ID único para a reserva
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Adicionar ao cache local com timestamp de expiração
  const expiresAt = Date.now() + ttl;
  temporaryBookings.set(tempId, {
    ...booking,
    tempId,
    expiresAt,
    status: 'pending'
  });
  
  // Configurar remoção automática após TTL
  setTimeout(() => {
    if (temporaryBookings.has(tempId)) {
      const booking = temporaryBookings.get(tempId);
      if (booking.status === 'pending') {
        console.log(`[recentBookingsCache] Removendo reserva temporária expirada: ${tempId}`);
        removeTemporaryBooking(tempId);
      }
    }
  }, ttl);
  
  // Invalidar consultas de disponibilidade afetadas
  atualizarCacheDisponibilidade(booking);
  
  console.log(`[recentBookingsCache] Reserva temporária adicionada: ${tempId}`);
  return tempId;
};

/**
 * Atualiza o status de uma reserva temporária
 * 
 * @param {string} tempId - ID da reserva temporária
 * @param {string} status - Novo status ('confirmed', 'failed', 'canceled')
 * @param {string} persistedId - ID do registro persistido no Firebase (opcional)
 */
export const updateTemporaryBooking = (tempId, status, persistedId = null) => {
  if (!temporaryBookings.has(tempId)) {
    console.warn(`[recentBookingsCache] Reserva temporária não encontrada: ${tempId}`);
    return;
  }
  
  const booking = temporaryBookings.get(tempId);
  
  // Atualizar status
  booking.status = status;
  
  // Se confirmado, adicionar ID persistido
  if (status === 'confirmed' && persistedId) {
    booking.persistedId = persistedId;
  }
  
  temporaryBookings.set(tempId, booking);
  console.log(`[recentBookingsCache] Reserva ${tempId} atualizada para status: ${status}`);
  
  // Se confirmado ou cancelado, não precisamos mais invalidar o cache
  if (status === 'confirmed' || status === 'canceled') {
    removeTemporaryBooking(tempId);
  }
};

/**
 * Remove uma reserva temporária do cache local
 * 
 * @param {string} tempId - ID da reserva temporária
 */
export const removeTemporaryBooking = (tempId) => {
  if (!temporaryBookings.has(tempId)) {
    return;
  }
  
  const booking = temporaryBookings.get(tempId);
  temporaryBookings.delete(tempId);
  
  // Se o status era "pending", precisamos restaurar a disponibilidade no cache
  if (booking.status === 'pending') {
    restaurarCacheDisponibilidade(booking);
  }
  
  console.log(`[recentBookingsCache] Reserva temporária removida: ${tempId}`);
};

/**
 * Atualiza o cache de disponibilidade após um novo agendamento
 * 
 * @param {Object} booking - Dados do agendamento
 */
export const atualizarCacheDisponibilidade = (booking) => {
  const { salonId, date, time, professionalId, services } = booking;
  
  if (!salonId || !date || !time || !professionalId || !services) {
    console.error('[recentBookingsCache] Dados insuficientes para atualizar cache:', booking);
    return;
  }
  
  try {
    // Formatar a data para o formato esperado
    const formattedDate = date instanceof Date 
      ? format(date, 'yyyy-MM-dd')
      : date;
    
    // Obter os IDs dos serviços
    const serviceIds = services.map(service => 
      typeof service === 'object' ? service.id : service
    );
    
    // Atualizar cache de horários disponíveis geral
    const availabilityKey = queryKeys.availability.byServiceAndDate(
      salonId,
      serviceIds,
      formattedDate
    );
    
    const timeSlotsCache = queryClient.getQueryData(availabilityKey);
    
    if (timeSlotsCache) {
      console.log(`[recentBookingsCache] Atualizando cache de horários para ${formattedDate}`);
      
      // Remover o horário que acabou de ser agendado
      const updatedTimeSlots = timeSlotsCache.filter(slot => 
        typeof slot === 'string' 
          ? slot !== time 
          : slot.time !== time
      );
      
      queryClient.setQueryData(availabilityKey, updatedTimeSlots);
    }
    
    // Atualizar cache de disponibilidade do profissional
    const professionalAvailabilityKey = queryKeys.professionals.availability(
      salonId,
      professionalId,
      formattedDate
    );
    
    const professionalSlotsCache = queryClient.getQueryData(professionalAvailabilityKey);
    
    if (professionalSlotsCache) {
      console.log(`[recentBookingsCache] Atualizando cache do profissional ${professionalId}`);
      
      // Se o cache for um array de slots
      if (Array.isArray(professionalSlotsCache)) {
        const updatedSlots = professionalSlotsCache.map(slot => {
          // Se o slot for um objeto com propriedade time
          if (typeof slot === 'object' && slot.time === time) {
            return { ...slot, available: false, hasConflicts: true };
          }
          // Se o slot for uma string
          if (typeof slot === 'string' && slot === time) {
            return { time, available: false, hasConflicts: true };
          }
          return slot;
        });
        
        queryClient.setQueryData(professionalAvailabilityKey, updatedSlots);
      } 
      // Se o cache for apenas um boolean de disponibilidade
      else if (typeof professionalSlotsCache === 'boolean') {
        queryClient.setQueryData(professionalAvailabilityKey, false);
      }
    }
    
    console.log(`[recentBookingsCache] Cache de disponibilidade atualizado com sucesso`);
  } catch (error) {
    console.error('[recentBookingsCache] Erro ao atualizar cache de disponibilidade:', error);
  }
};

/**
 * Restaura o cache de disponibilidade quando uma reserva temporária é removida
 * 
 * @param {Object} booking - Dados do agendamento
 */
export const restaurarCacheDisponibilidade = (booking) => {
  // Implementação similar ao atualizarCacheDisponibilidade, mas restaurando a disponibilidade
  // Apenas invalidamos os caches para que sejam recarregados na próxima consulta
  const { salonId, date, professionalId, services } = booking;
  
  if (!salonId || !date || !professionalId || !services) {
    return;
  }
  
  try {
    // Formatar a data
    const formattedDate = date instanceof Date 
      ? format(date, 'yyyy-MM-dd')
      : date;
    
    // Obter os IDs dos serviços
    const serviceIds = services.map(service => 
      typeof service === 'object' ? service.id : service
    );
    
    // Invalidar cache de horários disponíveis
    const availabilityKey = queryKeys.availability.byServiceAndDate(
      salonId,
      serviceIds,
      formattedDate
    );
    
    // Invalidar cache de disponibilidade do profissional
    const professionalAvailabilityKey = queryKeys.professionals.availability(
      salonId,
      professionalId,
      formattedDate
    );
    
    // Invalidar os caches para forçar recarregamento
    queryClient.invalidateQueries(availabilityKey);
    queryClient.invalidateQueries(professionalAvailabilityKey);
    
    console.log(`[recentBookingsCache] Cache de disponibilidade restaurado`);
  } catch (error) {
    console.error('[recentBookingsCache] Erro ao restaurar cache de disponibilidade:', error);
  }
};

/**
 * Verifica se um horário tem conflito com reservas temporárias pendentes
 * 
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:MM
 * @param {string} professionalId - ID do profissional
 * @returns {Object} - Resultado da verificação { hasConflict, conflictId }
 */
export const verificarConflito = (salonId, date, time, professionalId) => {
  // Verificar se há alguma reserva temporária ativa para este horário
  for (const [id, booking] of temporaryBookings.entries()) {
    // Ignorar reservas expiradas ou não pendentes
    if (booking.expiresAt < Date.now() || booking.status !== 'pending') {
      continue;
    }
    
    // Verificar se há conflito
    const sameDate = booking.date === date || 
      (booking.date instanceof Date && format(booking.date, 'yyyy-MM-dd') === date);
    
    if (sameDate && booking.time === time && booking.professionalId === professionalId) {
      return { 
        hasConflict: true, 
        conflictId: id,
        allowsOverlap: booking.allowsOverlap || false
      };
    }
  }
  
  return { hasConflict: false };
};

/**
 * Obtém todas as reservas temporárias ativas
 * 
 * @returns {Array} - Lista de reservas temporárias
 */
export const getActiveTemporaryBookings = () => {
  const now = Date.now();
  const activeBookings = [];
  
  for (const [id, booking] of temporaryBookings.entries()) {
    if (booking.expiresAt > now && booking.status === 'pending') {
      activeBookings.push(booking);
    }
  }
  
  return activeBookings;
};

/**
 * Limpa todas as reservas temporárias expiradas
 */
export const limparReservasExpiradas = () => {
  const now = Date.now();
  let count = 0;
  
  for (const [id, booking] of temporaryBookings.entries()) {
    if (booking.expiresAt < now) {
      removeTemporaryBooking(id);
      count++;
    }
  }
  
  if (count > 0) {
    console.log(`[recentBookingsCache] ${count} reservas temporárias expiradas foram removidas`);
  }
};

// Configurar limpeza periódica de reservas expiradas (a cada 30 segundos)
setInterval(limparReservasExpiradas, 30 * 1000); 